import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HoursInput } from "@/components/common/HoursInput";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const tomorrow = () => { const d = new Date(Date.now() + 864e5); d.setHours(9, 0, 0, 0); return d; };
const local = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 6e4).toISOString().slice(0, 16);

export const LeadAppointmentDialog = ({ open, onOpenChange, lead, onDone }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF({ company_id: lead.company_id || "", company_name: lead.company_name || "", title: lead.company_name || lead.contact_name || "",
      start: local(tomorrow()), duration: 60, location: "", outlook: true });
    setQ(lead.company_name || "");
  }, [open, lead]);
  useEffect(() => {
    if (!open || q.trim().length < 2) { setResults([]); return undefined; }
    const t = setTimeout(() => api.get("/crm/companies", { params: { q, limit: 5 } }).then((r) => setResults(r.data.items)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q, open]);
  if (!f) return null;
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const run = async () => {
    setBusy(true);
    try {
      const body = { title: f.title, start_at: new Date(f.start).toISOString(), duration_minutes: f.duration || 60, location: f.location || null, outlook: f.outlook,
        ...(f.company_id ? { company_id: f.company_id } : { company_name: f.company_name }) };
      const { data } = await api.post(`/crm/leads/${lead.id}/appointment`, body);
      toast.success("Appuntamento fissato: opportunità creata in pipeline");
      if (data.outlook_error) toast.warning(`Outlook: ${data.outlook_error}`);
      onOpenChange(false); onDone(data);
    } catch (e) { toast.error(errorMessage(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="lead-appointment-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Fissa appuntamento</DialogTitle>
          <DialogDescription>Il lead diventa qualificato: cliente e referente vengono creati (o collegati) e nasce l'opportunità in pipeline nella fase "Appuntamento".</DialogDescription></DialogHeader>
        <div className="space-y-3 py-1">
          <Label className="text-xs">Cliente (cerca esistente)</Label>
          <Input value={q} onChange={(e) => { setQ(e.target.value); set({ company_id: "" }); }} data-testid="lead-appt-search" />
          {results.length > 0 && <ul className="divide-y divide-line rounded-control border border-line">{results.map((r) => (
            <li key={r.id}><button type="button" onClick={() => set({ company_id: r.id })} className={`w-full px-3 py-2 text-left text-sm ${f.company_id === r.id ? "bg-brand-50 font-semibold text-ink" : "hover:bg-slate-50"}`} data-testid={`lead-appt-pick-${r.id}`}>{r.name}</button></li>))}</ul>}
          {!f.company_id && <Input placeholder="Ragione sociale nuovo cliente" value={f.company_name} onChange={(e) => set({ company_name: e.target.value })} data-testid="lead-appt-company-name" />}
          <div className="space-y-1.5"><Label className="text-xs">Titolo opportunità</Label><Input value={f.title} maxLength={200} onChange={(e) => set({ title: e.target.value })} data-testid="lead-appt-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Data e ora</Label><Input type="datetime-local" value={f.start} onChange={(e) => set({ start: e.target.value })} data-testid="lead-appt-start" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Durata</Label><HoursInput value={f.duration} onChange={(v) => set({ duration: v })} className="w-20" testId="lead-appt-duration" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Luogo / note</Label><Input value={f.location} maxLength={200} onChange={(e) => set({ location: e.target.value })} data-testid="lead-appt-location" /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.outlook} onChange={(e) => set({ outlook: e.target.checked })} data-testid="lead-appt-outlook" />Crea anche in Outlook</label>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={run} disabled={busy || !(f.duration >= 15 && f.duration <= 600) || f.title.trim().length < 2 || !f.start || (!f.company_id && f.company_name.trim().length < 2)} data-testid="lead-appt-confirm">Fissa appuntamento</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
